import React, { useState, useEffect } from 'react';
import { Stack, Box } from '@mui/material';
import MatchFinalise from './MatchFinalise';
import {
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import GameScoringStrokePlay from './GameScoringStrokePlay';
import GameScoringMatchPlay from './GameScoringMatchPlay';
const MatchContainer = ({
  gameData,
  users,
  isOrganiser,
  amountOfMatches,
  amountOfPlayers,
  onFinalise,
  onMatchesFinalised,
  userHasAcceptedADate,
}) => {
  const { db } = useFirebase();
  const [matchSlotPlayers, setMatchSlotPlayers] = useState({}); // { [matchNumber]: [slotPlayers] }
  const [matches, setMatches] = useState([]); // Array to store all match data
  const [localGameData, setLocalGameData] = useState(gameData); // Add local state for gameData
  const allMatchesFinalised = matches.every((match) => match?.status === 'finalised');

  // Update localGameData when gameData prop changes
  useEffect(() => {
    setLocalGameData(gameData);
  }, [gameData]);

  useEffect(() => {
    if (allMatchesFinalised) {
      onMatchesFinalised(localGameData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMatchesFinalised]);

  // Initialize matches and players from gameData if they exist
  useEffect(() => {
    const initializeMatches = () => {
      // If we have existing matches, use those
      if (localGameData.matches && localGameData.matches.length > 0) {
        setMatches(localGameData.matches);

        // Initialize slotPlayers from existing matches
        const initialSlotPlayers = {};
        localGameData.matches.forEach((match, index) => {
          if (match.players) {
            initialSlotPlayers[index + 1] = match.players.map((player) => ({
              user: player.uid || player.user || player.userId,
              userName: player.userName,
              group: player.group,
              handicap: player.handicap || 0,
            }));
          }
        });
        setMatchSlotPlayers(initialSlotPlayers);
      } else {
        // Initialize with default amount of matches
        const initialMatches = Array(amountOfMatches)
          .fill(null)
          .map(() => ({
            players: [],
            status: 'pending',
            date: Timestamp.fromDate(new Date()),
            format: localGameData.format,
            matchGroupingType: amountOfPlayers === '1v1' ? '1V1' : '2V2',
            teaTime: null,
          }));
        setMatches(initialMatches);

        // Instead of mutating gameData, update through parent callback
        if (onFinalise) {
          onFinalise({
            ...localGameData,
            matches: initialMatches,
          });
        }
      }
    };

    initializeMatches();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localGameData, amountOfMatches, amountOfPlayers]);

  // Handle changes to amountOfMatches
  useEffect(() => {
    // Only handle changes if we have existing matches
    if (matches.length > 0) {
      if (amountOfMatches > matches.length) {
        // Add new matches
        const newMatches = Array(amountOfMatches - matches.length)
          .fill(null)
          .map(() => ({
            players: [],
            status: 'pending',
            date: Timestamp.fromDate(new Date()),
            format: localGameData.format,
            matchGroupingType: amountOfPlayers === '1v1' ? '1V1' : '2V2',
            teaTime: null,
          }));
        setMatches([...matches, ...newMatches]);
      } else if (amountOfMatches < matches.length) {
        // Remove excess matches
        setMatches(matches.slice(0, amountOfMatches));
        // Also remove any slot players for removed matches
        const updatedSlotPlayers = { ...matchSlotPlayers };
        for (let i = amountOfMatches + 1; i <= matches.length; i++) {
          delete updatedSlotPlayers[i];
        }
        setMatchSlotPlayers(updatedSlotPlayers);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountOfMatches, matches.length]);

  // Compute all selected players with matchNumber
  const allSelectedPlayers = Object.entries(matchSlotPlayers).flatMap(([matchNumber, slots]) =>
    (slots || []).filter(Boolean).map((p) => ({ user: p.user, matchNumber: Number(matchNumber) }))
  );

  // Callback for MatchFinalise to update slotPlayers for a match
  const handleSlotPlayersChange = (slotPlayers, matchNumber) => {
    setMatchSlotPlayers((prev) => ({ ...prev, [matchNumber]: slotPlayers }));
  };

  const handleFinaliseAMatch = async ({
    slotPlayers,
    matchNumber,
    selectedFormat,
    matchGroupingType,
    teaTime,
    status,
  }) => {

    try {
      // Create a new match object with the required structure
      const newMatch = {
        players: slotPlayers.filter(Boolean).map((player) => ({
          uid: player.user,
          win: false,
          grossScore: 0,
          netScore: 0,
          group: player.group || (slotPlayers.indexOf(player) < 2 ? 'A' : 'B'),
          handicap: player.handicap || 0,
          waglRating: player.waglRating || 50,
        })),
        status: status || 'finalised',
        date: Timestamp.fromDate(new Date()),
        format: selectedFormat,
        matchGroupingType,
        teaTime,
      };

      // Update the matches array
      const updatedMatches = [...matches];
      updatedMatches[matchNumber - 1] = newMatch;

      // Create updated game data without mutating the original
      const updatedGameData = {
        ...localGameData,
        matches: updatedMatches,
        players: [
          ...(localGameData.players || []),
          ...slotPlayers
            .filter(Boolean)
            .filter((player) => !localGameData.players?.some((p) => p.user === player.user))
            .map((player) => ({
              user: player.user,
              userName: player.userName,
              handicap: player.handicap || 0,
              grossScore: updatedMatches
                .flatMap((m) => m.players)
                .filter((p) => p.user === player.user)
                .reduce((acc, cur) => (acc += cur.grossScore), 0),
              netScore: updatedMatches
                .flatMap((m) => m.players)
                .filter((p) => p.user === player.user)
                .reduce((acc, cur) => (acc += cur.netScore), 0),
              costs: 0,
              paid: false,
              didWin: false,
              group: player.group || (slotPlayers.indexOf(player) < 2 ? 'A' : 'B'),
            })),
        ],
      };

      // Update Firestore
      const gameRef = doc(db, 'games', localGameData.id);
      await updateDoc(gameRef, updatedGameData);

      // Update local state
      setMatches(updatedMatches);
      setLocalGameData(updatedGameData); // Update local game data

      // Call parent's onFinalise with the updated data
      if (onFinalise) {
        onFinalise(updatedGameData);
      }
    } catch (error) {
      console.error('Error updating match:', error);
    } finally {
    }
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={3} sx={{ mt: 1 }}>
        {matches.map((match, index) => {
          // console.log(
          //   'Rendering match:',
          //   index + 1,
          //   'Status:',
          //   match?.status,
          //   'Full match:',
          //   match
          // );
          // Force re-render when status changes
          const matchStatus = match?.status;
          let key = `${index}-${matchStatus}-score`;
          if (matchStatus === 'finalised' || matchStatus === 'completed') {
            return (
              <Box key={key}>
                {match.format === 'stroke' && (
                  <GameScoringStrokePlay
                    game={localGameData}
                    onFinalise={handleFinaliseAMatch}
                    users={users}
                    isOrganiser={isOrganiser}
                    matchNumber={index + 1}
                    slotPlayers={matchSlotPlayers[index + 1]}
                    teaTime={match.teaTime}
                    handleFinalise={handleFinaliseAMatch}
                    status={match.status}
                    matchGroupingType={false}
                    match={match}
                  ></GameScoringStrokePlay>
                )}
                {match.format === 'match' && (
                  <GameScoringMatchPlay
                    game={localGameData}
                    slotPlayers={matchSlotPlayers[index + 1]}
                    onFinalise={handleFinaliseAMatch}
                    users={users}
                    isOrganiser={isOrganiser}
                    alreadyScored={match.status === 'completed'}
                    matchNumber={index + 1}
                    matchGroupingType={match.matchGroupingType || ''}
                    teaTime={match.teaTime}
                    match={match}
                  ></GameScoringMatchPlay>
                )}
              </Box>
            );
          }
          key = `${index}-${matchStatus}-finalise`;
          return (
            <Box key={key}>
              <MatchFinalise
                gameData={localGameData}
                users={users}
                isOrganiser={isOrganiser}
                matchNumber={index + 1}
                allSelectedPlayers={allSelectedPlayers}
                onSlotPlayersChange={handleSlotPlayersChange}
                userHasAcceptedADate={userHasAcceptedADate}
                onFinalise={handleFinaliseAMatch}
              />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default MatchContainer;
